import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Announcement,
  PriorityHigh,
  SignalCellularAlt,
  SignalCellular0Bar,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface AnnouncementItem {
  id: number;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high';
  created_by_name: string;
  created_at: string;
}

const Announcements: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<AnnouncementItem | null>(null);
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: announcements = [] } = useQuery<AnnouncementItem[]>('announcements', async () => {
    const response = await api.get('/announcements');
    return response.data.announcements;
  });

  const createAnnouncementMutation = useMutation(
    async (announcementData: Partial<AnnouncementItem>) => {
      const response = await api.post('/announcements', announcementData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('announcements');
        enqueueSnackbar('Announcement created successfully!', { variant: 'success' });
        setOpenDialog(false);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to create announcement', { variant: 'error' });
      },
    }
  );

  const updateAnnouncementMutation = useMutation(
    async ({ id, data }: { id: number; data: Partial<AnnouncementItem> }) => {
      const response = await api.put(`/announcements/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('announcements');
        enqueueSnackbar('Announcement updated successfully!', { variant: 'success' });
        setOpenDialog(false);
        setEditingAnnouncement(null);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to update announcement', { variant: 'error' });
      },
    }
  );

  const deleteAnnouncementMutation = useMutation(
    async (id: number) => {
      await api.delete(`/announcements/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('announcements');
        enqueueSnackbar('Announcement deleted successfully!', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to delete announcement', { variant: 'error' });
      },
    }
  );

  const handleSubmit = (formData: FormData) => {
    const announcementData = {
      title: formData.get('title') as string,
      content: formData.get('content') as string,
      priority: formData.get('priority') as 'low' | 'medium' | 'high',
    };

    if (editingAnnouncement) {
      updateAnnouncementMutation.mutate({ id: editingAnnouncement.id, data: announcementData });
    } else {
      createAnnouncementMutation.mutate(announcementData);
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <PriorityHigh color="error" />;
      case 'medium':
        return <SignalCellularAlt color="warning" />;
      case 'low':
        return <SignalCellular0Bar color="info" />;
      default:
        return <SignalCellularAlt />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Announcements</Typography>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            New Announcement
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {announcements.map((announcement) => (
          <Grid item xs={12} md={6} key={announcement.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Announcement sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {announcement.title}
                  </Typography>
                  <Chip
                    icon={getPriorityIcon(announcement.priority)}
                    label={announcement.priority}
                    color={getPriorityColor(announcement.priority) as any}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {announcement.content}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  By {announcement.created_by_name} • {new Date(announcement.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>
              {user?.role === 'admin' && (
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => {
                      setEditingAnnouncement(announcement);
                      setOpenDialog(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => deleteAnnouncementMutation.mutate(announcement.id)}
                  >
                    Delete
                  </Button>
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
        </DialogTitle>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(new FormData(e.currentTarget)); }}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  defaultValue={editingAnnouncement?.title}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Content"
                  name="content"
                  multiline
                  rows={4}
                  defaultValue={editingAnnouncement?.content}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    name="priority"
                    label="Priority"
                    defaultValue={editingAnnouncement?.priority || 'medium'}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingAnnouncement ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Announcements; 